import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from 'config-hub-server/src/router/index.js';
import { useTRPC } from '../utils/trpc.js';

type Inputs = inferRouterInputs<AppRouter>;
type Outputs = inferRouterOutputs<AppRouter>;

export type UserRow = Outputs['users']['list']['users'][number];
export type GroupRow = Outputs['users']['list']['groups'][number];
export type GroupChangeResultView = Outputs['users']['changeGroups'];

/**
 * `pages/users/userView.ts#UserAction`, restated: `hooks/` may not import `pages/`
 * (`architecture.test.ts`), and this is the one place the five action names need to line up
 * with `runAction`'s switch below.
 */
type UserAction = 'reset_password' | 'resend_invite' | 'sign_out' | 'deactivate' | 'reactivate';

/**
 * Whether the Users page exists here at all, and whether this hub may change anything.
 * `users.status` never refuses, the same shape as `audit.status` / `assets.status`.
 */
export const useUsersConfig = () => {
    const trpc = useTRPC();
    // `staleTime: Infinity` — see `hooks/useWriteAccess.ts`.
    const query = useQuery(trpc.users.status.queryOptions(undefined, { meta: { quiet: true }, staleTime: Infinity }));
    return {
        configured: query.data?.configured ?? false,
        canManage: query.data?.canManage ?? false,
        groupPrefix: query.data?.groupPrefix ?? '',
        loading: query.isLoading,
        // The probe itself can fail (network/500), distinct from a clean "not configured" answer
        // — the page must tell those apart rather than reading a failed probe as "not configured".
        status: query.status,
        error: query.error,
        refetch: query.refetch,
    };
};

/** `keepPreviousData` so a filter change shows the previous rows until the new list lands. */
export const useUsersList = (input: Inputs['users']['list'], opts: { enabled?: boolean } = {}) => {
    const trpc = useTRPC();
    return useQuery({
        ...trpc.users.list.queryOptions(input),
        placeholderData: keepPreviousData,
        enabled: opts.enabled ?? true,
    });
};

/**
 * The writes. Every mutation invalidates the query families a write can touch when it settles
 * (`App.tsx`'s `MutationCache.onSuccess`, `utils/invalidation.ts`), so the list refetches from
 * Cognito after each one — the page never patches rows by hand.
 *
 * `invite` and `changeGroups` are quiet: their modals (`InviteUserModal`, `GroupChangeModal`)
 * already render a refusal inline, and a red toast on top would say it twice. The five account
 * actions below have no inline error of their own, so their failures are the ordinary toast.
 *
 * `changeGroups`, `deactivate`, `reactivate` and `signOut` can each act on the signed-in admin's
 * own account, but `users.me` is one of the four probes the global handler deliberately skips
 * (`utils/invalidation.ts`, `staleTime: Infinity` — see `hooks/useWriteAccess.ts`), so each invalidates it
 * explicitly here, unconditionally — cheaper than checking whether `username` is "self" and
 * always correct when it is.
 */
export const useUserMutations = () => {
    const trpc = useTRPC();
    const queryClient = useQueryClient();
    const invalidateMe = () => void queryClient.invalidateQueries({ queryKey: trpc.users.me.queryKey() });

    const invite = useMutation(trpc.users.invite.mutationOptions({ meta: { quiet: true } }));
    const changeGroups = useMutation(
        trpc.users.changeGroups.mutationOptions({ meta: { quiet: true }, onSuccess: invalidateMe }),
    );
    const deactivate = useMutation(trpc.users.deactivate.mutationOptions({ onSuccess: invalidateMe }));
    const reactivate = useMutation(trpc.users.reactivate.mutationOptions({ onSuccess: invalidateMe }));
    const signOut = useMutation(trpc.users.signOut.mutationOptions({ onSuccess: invalidateMe }));
    const resetPassword = useMutation(trpc.users.resetPassword.mutationOptions());
    const resendInvite = useMutation(trpc.users.resendInvite.mutationOptions());

    const runAction = (action: UserAction, username: string): Promise<UserRow> => {
        switch (action) {
            case 'deactivate':
                return deactivate.mutateAsync({ username });
            case 'reactivate':
                return reactivate.mutateAsync({ username });
            case 'sign_out':
                return signOut.mutateAsync({ username });
            case 'reset_password':
                return resetPassword.mutateAsync({ username });
            case 'resend_invite':
                return resendInvite.mutateAsync({ username });
        }
    };

    return { invite, changeGroups, runAction };
};
